import { useTranslation } from 'react-i18next'
import { motion, useReducedMotion } from 'framer-motion'
import { Pizza as PizzaIcon, Instagram } from 'lucide-react'
import { PageMeta } from '@/components/ui/PageMeta'
import { HeroSimple } from '@/components/ui/HeroSimple'
import { Footer } from '@/components/layout/Footer'

export function Pizza() {
  const { t, i18n } = useTranslation('pizza')
  const shouldReduceMotion = useReducedMotion()
  const lang = i18n.language as 'it' | 'en'
  const canonicalPath = `/${lang}/pizza-e-tas`

  const baseTransition = { duration: 0.6, ease: 'easeOut' as const }
  const fadeUp = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 24 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: '-80px' },
        transition: baseTransition,
      }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    name: 'Pizza e Tas',
    image: 'https://maiaetas.it/media/logo-pizza.jpg',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Piazzale Rimembranze',
      addressLocality: 'Crema',
      addressRegion: 'CR',
      postalCode: '26013',
      addressCountry: 'IT',
    },
    telephone: '+39-333-702-4046',
    servesCuisine: 'Italian',
    priceRange: '€€',
    url: `https://maiaetas.it${canonicalPath}`,
  }

  return (
    <>
      <PageMeta
        title={t('meta.title')}
        description={t('meta.description')}
        canonicalPath={canonicalPath}
        jsonLd={jsonLd}
        faviconVariant="pizza"
      />

      <main id="main-content">
        <HeroSimple
          backgroundColor="bg-pizza"
          textColor="text-white"
          icon={PizzaIcon}
          title={t('hero.title')}
          subtitle={t('hero.subtitle')}
          className="min-h-[50vh] md:min-h-[55vh]"
        />

        <section
          className="bg-pizza-soft px-4 py-[clamp(4rem,10vw,8rem)] sm:px-6 lg:px-8"
          aria-labelledby="pizza-about-heading"
        >
          <div className="mx-auto max-w-6xl">
            <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
              <motion.div {...fadeUp}>
                <h2
                  id="pizza-about-heading"
                  className="font-display text-[clamp(2.5rem,6vw,4rem)] font-extrabold leading-[1.05] tracking-tight text-ink"
                >
                  {t('about.title')}
                </h2>
                <p className="mt-6 text-lg leading-relaxed text-muted">
                  {t('about.description')}
                </p>
                <p className="mt-4 text-lg leading-relaxed text-muted">
                  {t('about.address')}
                </p>
              </motion.div>
              <motion.div
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: 0.15 }}
              >
                <div className="relative">
                  <div
                    className="absolute inset-0 rotate-2 rounded-[2rem] bg-pizza/20"
                    aria-hidden="true"
                  />
                  <div className="relative -rotate-1 rounded-[2rem] border-[6px] border-white bg-white p-2 shadow-xl">
                    <img
                      src="/media/pizza.jpg"
                      alt={t('about.imageAlt')}
                      className="aspect-[4/5] w-full rounded-[1.5rem] object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="bg-pizza px-4 py-16 text-center sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="mx-auto max-w-2xl">
            <h2 className="text-[clamp(1.75rem,4vw,3rem)] font-bold leading-tight tracking-tight text-white">
              {t('finalCta.title')}
            </h2>
            <p className="mt-4 text-lg text-white/90">{t('finalCta.subtitle')}</p>
            <a
              href="https://www.instagram.com/pizzaetas_crema/"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-3 text-sm font-semibold text-pizza transition-colors hover:bg-cream"
            >
              <Instagram className="h-4 w-4" aria-hidden="true" />
              {t('finalCta.cta')}
            </a>
          </motion.div>
        </section>
      </main>

      <Footer variant="pizza" />
    </>
  )
}
